import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Offcanvas, Form, Row, Col, InputGroup, Button, ButtonGroup, Badge } from 'react-bootstrap';
import Preloader from '../../components/Preloader/Preloader';
import SearchCardView from './SearchCardView';
import SearchTablePaginationView from './SearchTablePaginationView';
import OffcanvasTemplate from './OffcanvasTemplate';
import AutoComplete from '../../components/AutoComplete';
import { ProductContext } from '../../ProductProvider';
import { books_category } from '../../ProData';
import { useSnackbar } from 'notistack';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import './SearchMain.css';

const config = require('../../config.json');

export default function BooksSearch(props) {
  const { initialQuery, initialTopic } = props;
  const { validateLogin, loggedUser } = useContext(ProductContext);
  const { enqueueSnackbar } = useSnackbar();

  const [searchResult, setSearchResult] = useState([]);
  const [searchValue, setSearchValue] = useState(initialQuery || '');
  const [searchTopic, setSearchTopic] = useState(initialTopic || 'BookTitle');
  const [loaded, setLoaded] = useState(true);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [dispMsg, setDispMsg] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'
  const [clientFilter, setClientFilter] = useState('');

  // Offcanvas state
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);

  const [categoryList, setCategoryList] = useState([]);

  useEffect(() => {
    if (!loggedUser.isAuthenticated) {
      validateLogin();
    } else {
      const temp = books_category.filter((item) => item.categoryDisplay === 'Yes');
      setCategoryList(temp);
    }
  }, [loggedUser]);

  // Handle external topic clicks
  useEffect(() => {
    if (initialQuery) {
      setSearchValue(initialQuery);
      if (initialTopic) setSearchTopic(initialTopic);
      executeSearch(initialQuery, initialTopic || searchTopic);
    }
  }, [initialQuery, initialTopic]);

  const handleCloseDrawer = () => setShowDrawer(false);

  const handleShowDrawer = (event, item) => {
    if (event) event.preventDefault();
    setSelectedBook(item);
    setShowDrawer(true);
  };

  const executeSearch = async (val, topic) => {
    const q = (val !== undefined ? val : searchValue).trim();
    if (!q) {
      enqueueSnackbar('Please enter a search term', { variant: 'info' });
      return;
    }

    try {
      setLoaded(false);
      setDispMsg('');
      setSearchCompleted(false);

      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const clientID = loggedUser?.website || "VJCollege";
      const currentTopic = topic || searchTopic;
      const keysearchValue = currentTopic === "BookCategory" ? q : q.toUpperCase();
      let apiURL = `${config.api.invokeURL}/vjlibrary/search/books`;
      let successLabel = 'Book';

      if (currentTopic.startsWith('Article')) {
        apiURL = `${config.api.invokeURL}/vjlibrary/search/books/articles`;
        successLabel = 'Article';
      } else if (currentTopic === 'KeywordsArticle' || currentTopic === 'ArticleKeywords') {
        apiURL = `${config.api.invokeURL}/vjlibrary/search/books/articles/keyword`;
        successLabel = 'Article';
      } else if (currentTopic.startsWith('Keywords')) {
        apiURL = `${config.api.invokeURL}/vjlibrary/search/books/keyword`;
        successLabel = 'Book';
      }

      console.log("ApiURL :", apiURL, currentTopic);

      const response = await axios.get(apiURL, {
        params: {
          fetchKey: keysearchValue,
          fetchOption: currentTopic,
          clientID: clientID
        },
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });
      console.log("Response : ", response);

      const data = Array.isArray(response.data) ? response.data : [];
      setSearchResult(data);
      setDispMsg(`${data.length} ${successLabel}${data.length === 1 ? '' : 's'} Found`);
      enqueueSnackbar(`${data.length} ${successLabel}${data.length === 1 ? '' : 's'} Found`, { variant: 'success' });
      setSearchCompleted(true);
      setLoaded(true);
    } catch (error) {
      console.error("Error searching books/articles:", error);
      setLoaded(true);
      enqueueSnackbar("Error occurred: " + (error.response?.data?.message || error.message), { variant: 'warning' });
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    executeSearch();
  };

  const processCategorySelection = (item) => {
    if (item && item.name) {
      setSearchValue(item.name);
      executeSearch(item.name, "BookCategory");
    }
  };

  // Real-time In-page Filter
  const filteredBooks = useMemo(() => {
    if (!clientFilter.trim()) return searchResult;
    const term = clientFilter.toLowerCase();
    return searchResult.filter(b => {
      const title = (b.bookTitle || b.articleTitle || '').toLowerCase();
      const sub = (b.bookSubTitle || b.articleSubTitle || '').toLowerCase();
      const author = (b.bookAuthor || b.articleAuthor || '').toLowerCase();
      const callNo = `${b.bookClassificationNo || ''} ${b.authorMark || ''} ${b.callNo || ''}`.toLowerCase();
      const accNo = (b.accessionNo || '').toString().toLowerCase();
      const cat = (b.bookCategory || '').toLowerCase();
      const pub = (b.bookPublisherName || '').toLowerCase();
      const kw = (b.bookKeywords || b.articleKeywords || '').toLowerCase();
      const desc = (b.bookAbstract || b.articleDescription || '').toLowerCase();
      const artId = (b.articleID || '').toLowerCase();

      return title.includes(term) ||
        sub.includes(term) ||
        author.includes(term) ||
        callNo.includes(term) ||
        accNo.includes(term) ||
        cat.includes(term) ||
        pub.includes(term) ||
        kw.includes(term) ||
        desc.includes(term) ||
        artId.includes(term);
    });
  }, [searchResult, clientFilter]);

  const isArticleSearch = searchTopic.startsWith('Article') || searchTopic.startsWith('KeywordsArticle') || searchTopic === 'ArticleKeywords';

  const boundJournalCount = useMemo(() => {
    return searchResult.filter((b) => (b.bookAuthor || '').trim().toLowerCase() === 'bound journal').length;
  }, [searchResult]);

  const isSelectedBoundJournal = (selectedBook?.bookAuthor || '').trim().toLowerCase() === 'bound journal';

  return (
    <div>
      {!loaded && <Preloader show={true} />}

      {/* Book Search Form Box */}
      <div className="vj-search-box-card">
        <Form onSubmit={handleFormSubmit}>
          <Row className="g-3 align-items-center">
            <Col lg={3} md={4} sm={12}>
              <Form.Select
                className="vj-search-select w-100"
                value={searchTopic}
                onChange={(e) => setSearchTopic(e.target.value)}
              >
                <option value="BookTitle">📖 Book Title</option>
                <option value="BookAuthor">✍️ Author Name</option>
                <option value="BookCategory">🏷️ Category / Subject</option>
                <option value="AccessionNo">🔢 Book ID (Accession #)</option>
                <option value="CallNo">📍 Call Number / Shelf</option>
                <option value="Keywords">🔖 Book Keywords</option>
                <option value="ArticleTitle">📄 Article Title</option>
                <option value="KeywordsArticle">🏷️ Article Keywords</option>
              </Form.Select>
            </Col>

            <Col lg={6} md={5} sm={12}>
              {searchTopic === "BookCategory" ? (
                <div style={{ position: 'relative', zIndex: 100 }}>
                  <AutoComplete
                    data={categoryList}
                    onSelect={processCategorySelection}
                    placeholder="Type or select category (e.g. Theology, Scripture)..."
                    item={searchValue}
                    onChange={(val) => setSearchValue(val)}
                  />
                </div>
              ) : (
                <InputGroup>
                  <InputGroup.Text className="bg-light border-end-0">
                    <i className="bi bi-search text-muted"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    className="vj-search-input border-start-0"
                    placeholder={
                      searchTopic === "ArticleTitle"
                        ? "Enter article title to search..."
                        : searchTopic === "KeywordsArticle"
                        ? "Enter article keywords (e.g. THEOLOGY, DOGMATICS)..."
                        : searchTopic === "Keywords"
                        ? "Enter book keywords to search..."
                        : `Enter ${searchTopic.replace('Book', 'Book ')} to search...`
                    }
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                  />
                  {searchValue && (
                    <Button variant="outline-secondary" onClick={() => setSearchValue('')}>
                      <i className="bi bi-x"></i>
                    </Button>
                  )}
                </InputGroup>
              )}
            </Col>

            <Col lg={3} md={3} sm={12}>
              <button type="submit" className="btn-vj-search">
                <i className="bi bi-search"></i> {isArticleSearch ? 'Search Articles' : 'Search Books'}
              </button>
            </Col>
          </Row>
        </Form>
      </div>

      {/* Results Section */}
      {searchCompleted && (
        <div className="container px-0">
          {/* Results Toolbar */}
          <div className="vj-results-toolbar">
            <div className="d-flex flex-wrap align-items-center gap-3">
              <span className="vj-results-count">
                <i className="bi bi-journal-bookmark text-primary me-2"></i>
                {dispMsg}
              </span>
              {searchResult.length > 0 && (
                <span className="badge bg-light text-muted border px-2 py-1">
                  Showing {filteredBooks.length}
                </span>
              )}
              {boundJournalCount > 0 && (
                <Button
                  variant={clientFilter.toLowerCase() === 'bound journal' ? 'primary' : 'outline-primary'}
                  size="sm"
                  className="py-1 px-2 d-inline-flex align-items-center gap-1 shadow-sm"
                  style={{ borderRadius: '20px', fontSize: '0.78rem' }}
                  onClick={() => setClientFilter(clientFilter.toLowerCase() === 'bound journal' ? '' : 'Bound Journal')}
                  title={clientFilter.toLowerCase() === 'bound journal' ? 'Clear Bound Journal filter' : 'Filter results to only Bound Journals'}
                >
                  <i className="bi bi-journal-bookmark-fill"></i>
                  <span>Bound Journals</span>
                  <Badge
                    bg={clientFilter.toLowerCase() === 'bound journal' ? 'light' : 'primary'}
                    text={clientFilter.toLowerCase() === 'bound journal' ? 'dark' : 'white'}
                    pill
                  >
                    {boundJournalCount}
                  </Badge>
                </Button>
              )}
            </div>

            {searchResult.length > 0 && (
              <div className="d-flex align-items-center gap-2">
                {/* Instant Filter */}
                <div style={{ maxWidth: '240px' }}>
                  <InputGroup size="sm">
                    <InputGroup.Text className="bg-white">
                      <i className="bi bi-funnel text-muted"></i>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Filter results..."
                      value={clientFilter}
                      onChange={(e) => setClientFilter(e.target.value)}
                    />
                    {clientFilter && (
                      <Button variant="outline-secondary" onClick={() => setClientFilter('')}>
                        <i className="bi bi-x"></i>
                      </Button>
                    )}
                  </InputGroup>
                </div>

                {/* View Mode Toggle */}
                <ButtonGroup size="sm">
                  <Button
                    variant={viewMode === 'grid' ? 'primary' : 'outline-secondary'}
                    onClick={() => setViewMode('grid')}
                    title="Grid Cards View"
                  >
                    <i className="bi bi-grid-fill"></i>
                  </Button>
                  <Button
                    variant={viewMode === 'table' ? 'primary' : 'outline-secondary'}
                    onClick={() => setViewMode('table')}
                    title="Table List View"
                  >
                    <i className="bi bi-table"></i>
                  </Button>
                </ButtonGroup>
              </div>
            )}
          </div>

          {/* Results View */}
          {searchResult.length === 0 ? (
            <div className="text-center py-5 bg-white rounded-3 border p-4">
              <i className="bi bi-search text-muted fs-1 mb-2 d-block"></i>
              <h5>{isArticleSearch ? 'No Articles Found' : 'No Books Found'}</h5>
              <p className="text-muted small mb-0">
                {isArticleSearch
                  ? 'Try searching with different keywords, check the spelling, or search by Article Title.'
                  : 'Try searching with different keywords, check the spelling, or search by Category.'}
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <SearchCardView searchResult={filteredBooks} handleShow={handleShowDrawer} isArticle={isArticleSearch} />
          ) : (
            <SearchTablePaginationView
              searchResult={filteredBooks}
              setSearchResult={setSearchResult}
              handleShow={handleShowDrawer}
              isArticle={isArticleSearch}
            />
          )}
        </div>
      )}

      {/* Offcanvas Drawer for Book / Article Details */}
      <Offcanvas
        show={showDrawer}
        onHide={handleCloseDrawer}
        placement="end"
        scroll={true}
        style={{ width: '480px', maxWidth: '92vw' }}
      >
        <Offcanvas.Header closeButton className="vj-drawer-header">
          <Offcanvas.Title className="vj-drawer-title">
            <i className={`bi ${isArticleSearch || selectedBook?.articleID ? 'bi-file-earmark-text' : isSelectedBoundJournal ? 'bi-journal-bookmark-fill' : 'bi-book'} me-2`}></i>
            {isArticleSearch || selectedBook?.articleID
              ? 'Article & Book Details'
              : isSelectedBoundJournal
              ? 'Bound Journal Details'
              : 'Book Details'}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-3">
          {selectedBook && <OffcanvasTemplate selectedBook={selectedBook} />}
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
}
